import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CancelDrop,
  closestCenter,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
  DndContext,
  DragOverlay,
  DropAnimation,
  getFirstCollision,
  MouseSensor,
  TouchSensor,
  Modifiers,
  UniqueIdentifier,
  useSensors,
  useSensor,
  MeasuringStrategy,
  KeyboardCoordinateGetter,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';

import {
  AnimateLayoutChanges,
  SortableContext,
  useSortable,
  defaultAnimateLayoutChanges,
  verticalListSortingStrategy,
  SortingStrategy,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';

import { Item } from './components/folder/item';
import { Folder } from './components/folder/folder';

import { Section } from './types';
import throttle from 'lodash/throttle';
import { shallow } from 'zustand/shallow';
import { animated, useSpring } from '@react-spring/web';
import { useEditorStore } from './editor-store';
import { getHtmlText } from './helpers/get-html-text';
import { FolderProps } from './components/folder/folder';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { NeueAddSectionButton } from './add-section-button';

import { coordinateGetter as multipleContainersCoordinateGetter } from '../../utils/multipleFoldersKeyboardCoordinates';
import { Nullable } from 'src/types/nullable.type';
import { useUpdateEffect } from 'react-use';

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

type Items = Record<UniqueIdentifier, UniqueIdentifier[]>;

/* eslint-disable no-unused-vars */
enum DraggableActionEnum {
  IDLE = 0,
  MOVE_ITEM_OUT_OF_CONTAINER = 1,
  MOVE_ITEM_INSIDE_CONTAINER = 2,
  ORDER_CONTAINERS = 3,
  ORDER_SECTIONS = 4,
  ORDER_WITHIN_CONTAINER = 5,
}

interface DraggableMoveItemOutOfContainerActionProps {
  dragAction: DraggableActionEnum.MOVE_ITEM_OUT_OF_CONTAINER;
  direction: 'top' | 'bottom';
  emptyFolderId: string;
  activeId: string;
}

interface DraggableMoveItemInsideContainerActionProps {
  dragAction: DraggableActionEnum.MOVE_ITEM_INSIDE_CONTAINER;
  dragFolderId: UniqueIdentifier;
  containers: string[];
  items: Items;
}

interface DraggableOrderWithinContainerActionProps {
  dragAction: DraggableActionEnum.ORDER_WITHIN_CONTAINER;
  movedSectionId: UniqueIdentifier;
  newIndex: number;
}

interface DraggableOrderContainersActionProps {
  dragAction: DraggableActionEnum.ORDER_CONTAINERS;
}

interface DraggableIdleActionProps {
  dragAction: DraggableActionEnum.IDLE;
}

type DraggableAction =
  | DraggableMoveItemOutOfContainerActionProps
  | DraggableMoveItemInsideContainerActionProps
  | DraggableIdleActionProps
  | DraggableOrderWithinContainerActionProps
  | DraggableOrderContainersActionProps;

type LocalNonCriticalEditorState = Pick<ReturnType<typeof useEditorStore.getState>, 'isDraggingSomethingOnEditor'>;

function getNextFolderId() {
  return Math.random().toString(36).substring(2);
}

const convertArrayToObject = (sections: Section[]) => {
  const resultObject = {} as any;

  const sectionsWithFoldersId = sections.map((item, index) => {
    if (!item.folderID) {
      return { ...item, folderID: item.friendlyPath };
    }
    return item;
  });

  sectionsWithFoldersId.forEach((item) => {
    if (item && item.folderID) {
      const { folderID, id } = item;
      if (!resultObject[folderID]) {
        resultObject[folderID] = [];
      }
      resultObject[folderID].push(id);
    }
  });

  return resultObject;
};

const getRawSections = (items: Items, containers: string[]) => {
  const rawSections: UniqueIdentifier[] = [];

  containers.forEach((containerId) => {
    if (containerId in items) {
      rawSections.push(...items[containerId]);
    }
  });

  return rawSections;
};

const orderedSectionsGetter = (fn: (sectionId: UniqueIdentifier) => UniqueIdentifier | undefined) => {
  return (items: Items, containers: string[]) => {
    const rawSections = getRawSections(items, containers);

    return rawSections
      .map((sectionId) => {
        const folderKey = fn(sectionId);
        if (!folderKey) {
          return null;
        }

        return {
          id: sectionId,
          folder_key: folderKey,
        };
      })
      .filter(Boolean) as Array<{ id: string; folder_key: string }>;
  };
};

function DroppableContainer({
  children,
  columns = 1,
  disabled,
  id,
  items,
  style,
  label,
  activelyContainerId,
  onFolderNameChange,
  ...props
}: FolderProps & {
  disabled?: boolean;
  id: UniqueIdentifier;
  items: UniqueIdentifier[];
  style?: React.CSSProperties;
  activelyContainerId: UniqueIdentifier | null;
}) {
  const { active, attributes, listeners, over, setNodeRef, transition, transform } = useSortable({
    id,
    data: {
      type: 'container',
      children: items,
    },
    animateLayoutChanges,
  });
  const isOverContainer = over
    ? (id === over.id && active?.data.current?.type !== 'container') || items.includes(over.id)
    : false;

  const isActivelyOverContainer = activelyContainerId
    ? activelyContainerId === id || items.includes(activelyContainerId)
    : false;

  return (
    <Folder
      ref={disabled ? undefined : setNodeRef}
      isAlone={props.isAlone}
      style={{
        ...style,
        transition,
        transform: CSS.Translate.toString(transform),
      }}
      hover={isOverContainer && !isActivelyOverContainer}
      isActivelyOverContainer={isActivelyOverContainer}
      handleProps={{
        ...attributes,
        ...listeners,
      }}
      columns={columns}
      numberOfItems={items.length}
      label={label}
      onFolderNameChange={onFolderNameChange}
      {...props}
    >
      {children}
    </Folder>
  );
}

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {},
    },
  }),
};

interface Props {
  adjustScale?: boolean;
  cancelDrop?: CancelDrop;
  columns?: number;
  containerStyle?: React.CSSProperties;
  coordinateGetter?: KeyboardCoordinateGetter;
  getItemStyles?(args: {
    value: UniqueIdentifier;
    index: number;
    overIndex: number;
    isDragging: boolean;
    folderId: UniqueIdentifier;
    isSorting: boolean;
    isDragOverlay: boolean;
  }): React.CSSProperties;
  wrapperStyle?(args: { index: number }): React.CSSProperties;
  itemCount?: number;
  items?: Items;
  handle?: boolean;
  renderItem?: any;
  strategy?: SortingStrategy;
  modifiers?: Modifiers;
  minimal?: boolean;
  trashable?: boolean;
  scrollable?: boolean;
  vertical?: boolean;
}

export function MultipleFolders({
  cancelDrop,
  columns,
  handle = false,
  containerStyle,
  coordinateGetter = multipleContainersCoordinateGetter,
  getItemStyles = () => ({}),
  wrapperStyle = () => ({}),
  minimal = false,
  modifiers,
  renderItem,
  vertical = false,
  scrollable,
}: Props) {
  const layout = useEditorStore((state) => state.layout, shallow);
  const { sections } = layout;

  const [items, setItems] = useState<Items>([] as any);
  const [containers, setContainers] = useState([] as any);
  const [isDraggin, setIsDraggin] = useState(false);

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const recentlyMovedToNewContainer = useRef(false);
  const isSortingContainer = activeId ? containers.includes(activeId) : false;

  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
  const setContextMenuState = useEditorStore((state) => state.setContextMenuState);
  const setCurrentSectionId = useEditorStore((state) => state.setCurrentSectionId);
  const setScrollToSectionId = useEditorStore((state) => state.setScrollToSectionId);
  const currentSectionId = useEditorStore((state) => state.currentSectionId);

  const [localNonCriticalEditorState, setLocalNonCriticalEditorState] = useState<LocalNonCriticalEditorState>(
    useEditorStore.getState()
  );

  const [activelyContainerId, setActivelyContainerId] = useState<UniqueIdentifier | null>(null);
  const timeOutRefToResetOutsideRef = useRef<any>(null);
  const timeoutRef = useRef<any>(null);
  const containerAreaRef = useRef<Nullable<HTMLDivElement>>(null);

  const itemDraggablectionRef = useRef<DraggableAction>({
    dragAction: DraggableActionEnum.IDLE,
  });

  const throttleSetLocalNonCriticalEditorStateRef = React.useRef(
    throttle(setLocalNonCriticalEditorState, 1000, { leading: true, trailing: true })
  );

  const collisionDetectionStrategy: CollisionDetection = useCallback(
    (args) => {
      if (activeId && activeId in items) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((container) => container.id in items),
        });
      }

      const pointerIntersections = pointerWithin(args);
      const intersections = pointerIntersections.length > 0 ? pointerIntersections : rectIntersection(args);
      let overId = getFirstCollision(intersections, 'id');

      if (overId != null) {
        if (overId in items) {
          const containerItems = items[overId];

          if (containerItems.length > 0) {
            overId = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (container) => container.id !== overId && containerItems.includes(container.id)
              ),
            })[0]?.id;
          }
        }

        lastOverId.current = overId;

        return [{ id: overId }];
      }

      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId;
      }

      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [activeId, items]
  );

  const [clonedItems, setClonedItems] = useState<Items | null>(null);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  useUpdateEffect(() => {
    if (!containers || itemDraggablectionRef.current.dragAction === DraggableActionEnum.IDLE) {
      return;
    }

    if (itemDraggablectionRef.current.dragAction === DraggableActionEnum.MOVE_ITEM_OUT_OF_CONTAINER) {
      const { activeId, emptyFolderId } = itemDraggablectionRef.current;
      let { length: newItemIndex } = containers;
      const isItemDraggedToTop = itemDraggablectionRef.current.direction === 'top';
      if (isItemDraggedToTop) {
        newItemIndex = 0;
      }
      dispatchUserEditorAction({
        type: 'folder-update',
        id: activeId,
        folder_key: emptyFolderId,
      });

      dispatchUserEditorAction({
        type: 'move-section',
        id: activeId,
        newIndex: isItemDraggedToTop ? newItemIndex : newItemIndex - 1,
      });
    } else if (itemDraggablectionRef.current.dragAction === DraggableActionEnum.MOVE_ITEM_INSIDE_CONTAINER) {
      const { containers, items } = itemDraggablectionRef.current;
      const orderedSections = getOrderedSections(items, containers);

      dispatchUserEditorAction({
        type: 'folders-update',
        sections: orderedSections,
      });
    } else if (itemDraggablectionRef.current.dragAction === DraggableActionEnum.ORDER_WITHIN_CONTAINER) {
      dispatchUserEditorAction({
        type: 'move-section',
        id: `${itemDraggablectionRef.current.movedSectionId}`,
        newIndex: itemDraggablectionRef.current.newIndex,
      });
    } else if (itemDraggablectionRef.current.dragAction === DraggableActionEnum.ORDER_CONTAINERS) {
      const rawSections = getRawSections(items, containers);
      dispatchUserEditorAction({
        type: 'move-sections',
        payload: rawSections.map((id, index) => ({ id: `${id}`, newIndex: index })),
      });
    }

    itemDraggablectionRef.current = {
      dragAction: DraggableActionEnum.IDLE,
    };
  }, [containers]);

  useEffect(() => {
    const unsubscribe = useEditorStore.subscribe(
      (state) => {
        return {
          isDraggingSomethingOnEditor: state.isDraggingSomethingOnEditor,
        };
      },
      throttleSetLocalNonCriticalEditorStateRef.current,
      { equalityFn: shallow }
    );
    return () => {
      throttleSetLocalNonCriticalEditorStateRef.current.cancel();
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [items]);

  useEffect(() => {
    if (itemDraggablectionRef.current.dragAction !== DraggableActionEnum.IDLE) {
      return;
    }
    const convertedSections = convertArrayToObject(sections);
    setItems(convertedSections);
    setContainers(Object.keys(convertedSections));
  }, [sections]);

  const findContainer = (id: UniqueIdentifier) => {
    if (id in items) {
      return id;
    }

    return Object.keys(items).find((key) => items[key].includes(id));
  };

  const getItemIndex = (id: UniqueIdentifier) => {
    const container = findContainer(id);

    if (!container) {
      return -1;
    }

    const index = items[container].indexOf(id);

    return index;
  };

  const onDragCancel = () => {
    if (clonedItems) {
      setItems(clonedItems);
    }

    setActiveId(null);
    setClonedItems(null);
    setIsDraggin(false);
  };

  const pullNonCriticalEditorStateImmediately = useRefCallback(() => {
    setLocalNonCriticalEditorState(useEditorStore.getState() as LocalNonCriticalEditorState);
  }, []);

  const onSectionCardClick = (id: string) => {
    setCurrentSectionId(id);
    setScrollToSectionId(id);
    pullNonCriticalEditorStateImmediately();
  };

  const getFolderName = (folderId: UniqueIdentifier) => {
    const folder = sections.filter((section) => section.folderID === folderId && section.folderName !== null);
    if (folder[0]?.folderName) {
      return folder[0].folderName;
    }
    return 'New section';
  };

  function handleRemove(folderId: UniqueIdentifier) {
    setContainers((containers: UniqueIdentifier[]) => containers.filter((id) => id !== folderId));

    sections
      .filter((section) => section.folderID === folderId)
      .map((item) =>
        dispatchUserEditorAction({
          type: 'delete-section',
          id: item.id,
        })
      );
  }

  const getOrderedSections = orderedSectionsGetter(findContainer);

  const handleFolderNameChange = (folderId: UniqueIdentifier, name: string) => {
    sections
      .filter((section) => section.folderID === folderId)
      .map((item) => {
        dispatchUserEditorAction({
          type: 'folder-set-name',
          id: item.id as string,
          folder_name: getHtmlText(name),
        });
      });
  };

  const [scrollValues] = useSpring(
    () => ({
      scrollTopFadeOpacity: 0,
      scrollBottomFadeOpacity: 0,
    }),
    []
  );

  function renderContainerDragOverlay(folderId: UniqueIdentifier) {
    return (
      <Folder
        label={getFolderName(folderId)}
        columns={columns}
        shadow
        unstyled={false}
        onFolderNameChange={() => {}}
        numberOfItems={items[folderId].length}
        isDraggin
        isAlone={containers.length === 1}
      >
        {items[folderId].map((item, index) => (
          <Item
            key={item}
            value={item}
            handle={handle}
            style={getItemStyles({
              folderId,
              overIndex: -1,
              index: getItemIndex(item),
              value: item,
              isDragging: false,
              isSorting: false,
              isDragOverlay: false,
            })}
            wrapperStyle={wrapperStyle({ index })}
            renderItem={renderItem}
            folderID={folderId}
            rect={{} as any}
            current={false}
            onCardClick={() => {}}
            onNameChange={() => {}}
            setContextMenuState={(pageX: number, pageY: number, renameAction: () => void) => {}}
          />
        ))}
      </Folder>
    );
  }

  function renderSortableItemDragOverlay(id: Nullable<UniqueIdentifier>) {
    if (!id) {
      return null;
    }
    return (
      <Item
        value={id}
        handle={handle}
        wrapperStyle={wrapperStyle({ index: 0 })}
        dragOverlay
        current={false}
        rect={{} as any}
        onCardClick={() => {}}
        onNameChange={() => {}}
        setContextMenuState={() => {}}
        folderID={findContainer(id) as UniqueIdentifier}
      />
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      <animated.div
        style={{
          display: 'inline-grid',
          boxSizing: 'border-box',
          padding: '20px 0 20px 20px',
          gridAutoFlow: vertical ? 'row' : 'column',
          maxHeight: '75vh',
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          position: 'relative',
        }}
        className='styledScrollbar'
      >
        <div className='flex flex-col gap-4 pr-4' ref={containerAreaRef}>
          <animated.div
            className='absolute h-12 left-0 top-0 right-0 pointer-events-none bg-gradient-to-b from-neue-canvas-bg to-transparent'
            style={{
              opacity: scrollValues.scrollTopFadeOpacity,
            }}
          ></animated.div>
          <animated.div
            className='absolute h-12 left-0 bottom-0 right-0 pointer-events-none bg-gradient-to-t from-neue-canvas-bg to-transparent'
            style={{
              opacity: scrollValues.scrollBottomFadeOpacity,
            }}
          ></animated.div>
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetectionStrategy}
            measuring={{
              droppable: {
                strategy: MeasuringStrategy.Always,
              },
            }}
            onDragStart={({ active }) => {
              setIsDraggin(true);
              setActiveId(active.id);
              setClonedItems(items);
            }}
            onDragMove={({ active }) => {
              if (!containerAreaRef.current) {
                return;
              }

              const containerRect = containerAreaRef.current.getBoundingClientRect();
              const { translated: activeRect } = active.rect.current;

              if (!activeRect) {
                return;
              }

              const isOutsideFromTop = activeRect.top < containerRect.top;
              const isOutsideFromBottom = activeRect.bottom > containerRect.bottom;

              if (isOutsideFromBottom || isOutsideFromTop) {
                itemDraggablectionRef.current = {
                  dragAction: DraggableActionEnum.MOVE_ITEM_OUT_OF_CONTAINER,
                  direction: isOutsideFromTop ? 'top' : 'bottom',
                  emptyFolderId: '',
                  activeId: '',
                };
              }
            }}
            onDragOver={({ active, over, delta }) => {
              if (delta.x === 0 && delta.y === 0) {
                return;
              }
              const overId = over?.id;
              const activeId = active?.id;

              if (!overId || (!!(activeId in items) && !!items[activeId])) {
                return;
              }

              const activeContainer = findContainer(active.id);
              const overContainer = findContainer(overId);

              if (!activeContainer || !overContainer || activeContainer === overContainer) {
                return;
              }

              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                setActivelyContainerId(null);
              }
              timeoutRef.current = setTimeout(() => {
                itemDraggablectionRef.current = {
                  dragAction: DraggableActionEnum.MOVE_ITEM_INSIDE_CONTAINER,
                  dragFolderId: overId,
                  containers: [],
                  items: {},
                };
                setActivelyContainerId(overId);
              }, 450);
            }}
            onDragEnd={({ active, over, delta }) => {
              if (delta.x === 0 && delta.y === 0) {
                return;
              }
              const activeContainer = findContainer(active.id);
              if (!activeContainer) {
                setActiveId(null);
                return;
              }

              const overId = over?.id;
              if (overId == null) {
                setActiveId(null);
                return;
              }

              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                setActivelyContainerId(null);
              }

              const overContainer = findContainer(overId);

              if (
                itemDraggablectionRef.current.dragAction === DraggableActionEnum.MOVE_ITEM_OUT_OF_CONTAINER &&
                active.data.current?.type !== 'container'
              ) {
                clearTimeout(timeOutRefToResetOutsideRef.current);
                const { direction } = itemDraggablectionRef.current;
                const emptyFolderId = getNextFolderId();
                const newItems = { ...items };
                let newContainers = [...containers];
                const itemIndexToRemove = newItems[activeContainer].indexOf(active.id);
                if (newItems[activeContainer].length === 0) {
                  delete newItems[activeContainer];
                  newContainers = newContainers.filter((container) => container !== activeContainer);
                }
                newItems[activeContainer].splice(itemIndexToRemove, 1);
                newItems[emptyFolderId] = [active.id];

                itemDraggablectionRef.current.activeId = active.id as string;
                itemDraggablectionRef.current.emptyFolderId = emptyFolderId;

                if (direction === 'top') {
                  newContainers.unshift(emptyFolderId);
                } else {
                  newContainers.push(emptyFolderId);
                }

                setContainers(newContainers);
                setItems(newItems);
                return;
              }

              if (overContainer) {
                if (overContainer === activeContainer) {
                  const overIndex = items[overContainer].indexOf(overId);
                  const activeIndex = items[overContainer].indexOf(active.id);

                  // the dragged container's index hasn't changed when it's dropped
                  if ([overIndex, activeIndex].every((index) => index < 0)) {
                    return;
                  }

                  const newItems = { ...items };
                  const tempOverItem = newItems[overContainer][overIndex];

                  newItems[overContainer][overIndex] = newItems[overContainer][activeIndex];
                  newItems[overContainer][activeIndex] = tempOverItem;

                  itemDraggablectionRef.current = {
                    dragAction: DraggableActionEnum.ORDER_WITHIN_CONTAINER,
                    movedSectionId: active.id,
                    newIndex: activeIndex,
                  };
                  setItems(newItems);
                  setContainers([...containers]);
                  return;
                }
                // container being dragged
                if (items[active.id]) {
                  const activeContainerIndex = containers.indexOf(activeContainer);
                  const overContainerIndex = containers.indexOf(overContainer);

                  const newContainers = [...containers];
                  newContainers.splice(activeContainerIndex, 1);
                  newContainers.splice(overContainerIndex, 0, activeContainer);

                  itemDraggablectionRef.current = {
                    dragAction: DraggableActionEnum.ORDER_CONTAINERS,
                  };

                  setContainers(newContainers);
                }

                // item being dragged
                const activeIndex = items[activeContainer].indexOf(active.id);
                const overIndex = items[overContainer].indexOf(overId);

                if (activelyContainerId) {
                  // move to inside over container
                  const newItems = { ...items };
                  let newContainers = containers;

                  const [removed] = newItems[activeContainer]?.splice(activeIndex, 1);
                  const newIndex = overIndex + 1;

                  newItems[overContainer]?.splice(newIndex, 0, removed);

                  if (newItems[activeContainer]?.length === 0 && newContainers) {
                    delete newItems[activeContainer];
                    newContainers = newContainers?.filter((container: string) => container != activeContainer);
                  }

                  // it's already MOVE_ITEM_INSIDE_CONTAINER. This condition is to convince TypeScript
                  if (itemDraggablectionRef.current.dragAction === DraggableActionEnum.MOVE_ITEM_INSIDE_CONTAINER) {
                    itemDraggablectionRef.current = {
                      dragAction: DraggableActionEnum.MOVE_ITEM_INSIDE_CONTAINER,
                      dragFolderId: itemDraggablectionRef.current.dragFolderId,
                      containers: newContainers,
                      items: newItems,
                    };
                  }
                  setItems(newItems);
                  setContainers(newContainers);
                }
              }
              setActivelyContainerId(null);
              setActiveId(null);
              setIsDraggin(false);
            }}
            cancelDrop={cancelDrop}
            onDragCancel={onDragCancel}
            modifiers={modifiers}
          >
            <SortableContext
              items={[...containers]}
              strategy={vertical ? verticalListSortingStrategy : horizontalListSortingStrategy}
            >
              {containers.map((folderId: string) => (
                <DroppableContainer
                  key={folderId}
                  id={folderId}
                  label={getFolderName(folderId)}
                  columns={columns}
                  items={items[folderId]}
                  scrollable={scrollable}
                  style={containerStyle}
                  unstyled={minimal}
                  onFolderNameChange={(name) => handleFolderNameChange(folderId, name)}
                  onRemove={() => handleRemove(folderId)}
                  isMoving={isDraggin}
                  activelyContainerId={activelyContainerId}
                  isAlone={containers.length === 1}
                >
                  <SortableContext items={items[folderId]}>
                    {items[folderId].map((value, index) => {
                      return (
                        <SortableItem
                          disabled={isSortingContainer}
                          key={value}
                          id={value}
                          index={index}
                          handle={handle}
                          style={getItemStyles}
                          wrapperStyle={wrapperStyle}
                          renderItem={renderItem}
                          folderId={folderId}
                          isFolder={items[folderId]?.length > 1}
                          getIndex={getItemIndex}
                          current={value === currentSectionId}
                          onNameChange={(name) => {
                            dispatchUserEditorAction({
                              type: 'set-section-name',
                              id: value as string,
                              name: getHtmlText(name),
                              setManually: true,
                            });
                          }}
                          onCardClick={() => onSectionCardClick(value as string)}
                          setContextMenuState={(pageX: number, pageY: number, renameAction: () => void) =>
                            setContextMenuState({
                              entity: {
                                type: 'section-card',
                                sectionId: value as string,
                                renameAction,
                                items: items[folderId] as string[],
                              },
                              pageX,
                              pageY,
                            })
                          }
                        />
                      );
                    })}
                  </SortableContext>
                  {activeId && !containers.includes(activeId) && (
                    <DragOverlay dropAnimation={dropAnimation}>{renderSortableItemDragOverlay(activeId)}</DragOverlay>
                  )}
                </DroppableContainer>
              ))}
            </SortableContext>
            {activeId && containers.includes(activeId) && (
              <DragOverlay dropAnimation={dropAnimation}>{renderContainerDragOverlay(activeId)}</DragOverlay>
            )}
          </DndContext>
        </div>
      </animated.div>
      {minimal ? undefined : (
        <div className='relative pl-11 flex items-center justify-start'>
          <NeueAddSectionButton forceRefreshSectionBar={pullNonCriticalEditorStateImmediately} />
        </div>
      )}
    </div>
  );
}

interface SortableItemProps {
  folderId: UniqueIdentifier;
  id: UniqueIdentifier;
  index: number;
  handle: boolean;
  disabled?: boolean;
  current: boolean;
  isFolder?: boolean;
  onRemove?(): void;
  onCardClick(): void;
  renderItem(): React.ReactElement;
  onNameChange: (name: string) => void;
  style(args: any): React.CSSProperties;
  getIndex(id: UniqueIdentifier): number;
  wrapperStyle({ index }: { index: number }): React.CSSProperties;
  setContextMenuState: (pageX: number, pageY: number, renameAction: () => void) => void;
}

function useMountStatus() {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setIsMounted(true), 500);

    return () => clearTimeout(timeout);
  }, []);

  return isMounted;
}

function SortableItem({
  disabled,
  id,
  index,
  handle,
  renderItem,
  style,
  folderId,
  isFolder,
  getIndex,
  wrapperStyle,
  current,
  onCardClick,
  onNameChange,
  setContextMenuState,
  ...props
}: SortableItemProps) {
  const { setNodeRef, setActivatorNodeRef, listeners, isDragging, isSorting, over, overIndex, transform, transition } =
    useSortable({ id });

  const mounted = useMountStatus();
  const mountedWhileDragging = isDragging && !mounted;

  return (
    <Item
      ref={disabled ? undefined : setNodeRef}
      value={id}
      dragging={isDragging}
      sorting={isSorting}
      handle={handle}
      handleProps={handle ? { ref: setActivatorNodeRef } : undefined}
      index={index}
      wrapperStyle={wrapperStyle({ index })}
      style={style({
        index,
        value: id,
        isDragging,
        isSorting,
        overIndex: over ? getIndex(over.id) : overIndex,
        folderId,
      })}
      transition={transition}
      transform={transform}
      fadeIn={mountedWhileDragging}
      listeners={listeners}
      renderItem={renderItem}
      onCardClick={onCardClick}
      setContextMenuState={setContextMenuState}
      folderID={folderId}
      isFolder={isFolder}
      rect={{} as any}
      current={current}
      onNameChange={onNameChange}
      {...props}
    />
  );
}
